import React, {useEffect, useRef, useState} from 'react';
import {RouteComponentProps, useHistory, useLocation} from 'react-router-dom';
import {
    IonButtons,
    IonHeader,
    IonPage,
} from '@ionic/react';
import {StyledIonContent, StyledIonTitle, StyledIonToolbar} from '../../components/content/content.style';
import {StyledHeaderButton, StyledHeaderButtonImage, StyledBlueButton} from '../../components/button/button.style';
import {ArrowLeftImage} from '../../utils/styles/icons';
import {useTranslation} from 'react-i18next';
import i18next from 'i18next';
import LocationModal from '../../modals/location/location.component';
import {StyledSpecificationViewer} from './specificationViewerPage.style';
import {SpecificationViewData, AcknowledgmentStatus} from '../../models/specificationViewer';
import {getSpecificationView, getAcknowledgmentStatus as fetchAcknowledgmentStatus} from '../../services/specificationViewer.service';
import SpecHeader from './components/specHeader.component';
import SectionSafety from './components/sectionSafety.component';
import SectionPacking from './components/sectionPacking.component';
import SectionProcedure from './components/sectionProcedure.component';
import SectionActivities from './components/sectionActivities.component';
import SpecFiles from './components/specFiles.component';
import AcknowledgeBar from './components/acknowledgeBar.component';
import StaticPane from '../../components/pane/static-pane.component';

import {ReactComponent as ArrowDownIcon} from '../../assets/images/travelAllowance/arrow-down.svg';

interface RouterProps {
    specificationId: string;
    reportId: string;
}

interface LocationState {
    signatureRequired?: boolean;
    clientId?: string | number | bigint;
    returnTo?: string;
}

interface SpecificationViewerProps extends RouteComponentProps<RouterProps> {
}

const SpecificationViewerPage: React.FC<SpecificationViewerProps> = ({match}) => {
    const history = useHistory();
    const location = useLocation<LocationState>();
    const {t} = useTranslation();

    const {params: {specificationId, reportId}} = match;
    const signatureRequestedByCaller = location.state?.signatureRequired ?? true;
    const clientId = location.state?.clientId;
    const returnTo = location.state?.returnTo;

    const header = useRef<HTMLIonHeaderElement>(null);
    const [topEdge, updateTopEdge] = useState<number | undefined>(undefined);
    const contentRef = useRef<HTMLIonContentElement>(null);
    const bottomSentinelRef = useRef<HTMLDivElement>(null);

    const [specData, setSpecData] = useState<SpecificationViewData | null>(null);
    const [acknowledgment, setAcknowledgment] = useState<AcknowledgmentStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [scrolledToBottom, setScrolledToBottom] = useState(false);
    const [showLocationModal, setShowLocationModal] = useState(false);

    // A signature is only ever possible for an ACCEPTED (verified) specification, regardless
    // of what the caller requested — an unverified spec must always be viewable without signing.
    const signatureRequired = signatureRequestedByCaller && specData?.status === 'ACCEPTED';

    // Section expand states
    const [safetyExpanded, setSafetyExpanded] = useState(true);
    const [packingExpanded, setPackingExpanded] = useState(false);
    const [procedureExpanded, setProcedureExpanded] = useState(false);
    const [activitiesExpanded, setActivitiesExpanded] = useState(false);
    const [filesExpanded, setFilesExpanded] = useState(false);

    const updateHeightTimeoutRef = useRef<number | null>(null);

    const updateHeight = () => {
        if (updateHeightTimeoutRef.current) {
            window.clearTimeout(updateHeightTimeoutRef.current);
            updateHeightTimeoutRef.current = null;
        }
        if (header.current?.clientHeight == 0) {
            updateHeightTimeoutRef.current = window.setTimeout(updateHeight);
        } else {
            updateTopEdge(header.current?.clientHeight);
        }
    };

    useEffect(() => {
        updateHeight();
    });

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (loading || !specData || !bottomSentinelRef.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setScrolledToBottom(true); },
            {threshold: 0.1}
        );
        observer.observe(bottomSentinelRef.current);
        return () => observer.disconnect();
    }, [loading, specData]);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            const viewResponse = await getSpecificationView(specificationId, i18next.language);
            setSpecData(viewResponse.data);
        } catch (e) {
            if ((e as any)?.response?.status === 403) {
                setError('gps');
            } else if ((e as any)?.response?.status === 404) {
                setError('notFound');
            } else {
                setError('generic');
            }
            setLoading(false);
            return;
        }

        try {
            const statusResponse = await fetchAcknowledgmentStatus(specificationId);
            setAcknowledgment(statusResponse.data);
        } catch (e) {
            // acknowledgment status fetch failed - continue without it
        }

        setLoading(false);
    };

    const renderSection = (
        title: string,
        expanded: boolean,
        setExpanded: (v: boolean) => void,
        content: React.ReactNode
    ) => (
        <div className="section">
            <div className="section-header" onClick={() => setExpanded(!expanded)}>
                <div className="section-title">{title}</div>
                <ArrowDownIcon className={`section-arrow ${expanded ? 'open' : ''}`}/>
            </div>
            {expanded && <div className="section-content">{content}</div>}
        </div>
    );

    const renderError = () => {
        if (error === 'gps') {
            return (
                <div className="error-container">
                    <div className="error-title">{t('specificationViewer.gpsErrorTitle')}</div>
                    <div className="error-message">{t('specificationViewer.gpsErrorMessage')}</div>
                    <StyledBlueButton onClick={() => setShowLocationModal(true)}>
                        {t('specificationViewer.startLocalization')}
                    </StyledBlueButton>
                    <StyledBlueButton onClick={loadData}>
                        {t('specificationViewer.retry')}
                    </StyledBlueButton>
                </div>
            );
        }
        if (error === 'notFound') {
            return (
                <div className="error-container">
                    <div className="error-title">{t('specificationViewer.notFoundTitle')}</div>
                    <div className="error-message">{t('specificationViewer.notFoundMessage')}</div>
                </div>
            );
        }
        return (
            <div className="error-container">
                <div className="error-title">{t('specificationViewer.errorTitle')}</div>
                <div className="error-message">{t('specificationViewer.errorMessage')}</div>
                <StyledBlueButton onClick={loadData}>
                    {t('specificationViewer.retry')}
                </StyledBlueButton>
            </div>
        );
    };

    return (
        <IonPage>
            <StyledIonContent ref={contentRef}>
                <IonHeader ref={header} className="ion-no-border">
                    <StyledIonToolbar>
                        {!returnTo && (
                            <IonButtons slot="start">
                                <StyledHeaderButton onClick={() => history.goBack()}>
                                    <StyledHeaderButtonImage src={ArrowLeftImage}/>
                                </StyledHeaderButton>
                            </IonButtons>
                        )}
                        <StyledIonTitle>{t('specificationViewer.title')}</StyledIonTitle>
                    </StyledIonToolbar>
                </IonHeader>
                <StaticPane topEdge={topEdge} marginTop={40} hideGrabber={true}>
                    {loading && !error && (
                        <StyledSpecificationViewer>
                            <div className="error-container">
                                <div className="error-message">{t('common.loading')}</div>
                            </div>
                        </StyledSpecificationViewer>
                    )}

                    {error && (
                        <StyledSpecificationViewer>
                            {renderError()}
                        </StyledSpecificationViewer>
                    )}

                    {!loading && !error && specData && (
                        <StyledSpecificationViewer className={signatureRequired ? 'with-acknowledge-bar' : ''}>
                            <SpecHeader spec={specData}/>

                            {renderSection(
                                t('specificationViewer.sectionSafety'),
                                safetyExpanded,
                                setSafetyExpanded,
                                <SectionSafety spec={specData}/>
                            )}

                            {renderSection(
                                t('specificationViewer.sectionPacking'),
                                packingExpanded,
                                setPackingExpanded,
                                <SectionPacking spec={specData}/>
                            )}

                            {renderSection(
                                t('specificationViewer.sectionProcedure'),
                                procedureExpanded,
                                setProcedureExpanded,
                                <SectionProcedure procedure={specData.invalidProductProcedure}/>
                            )}

                            {renderSection(
                                t('specificationViewer.sectionActivities'),
                                activitiesExpanded,
                                setActivitiesExpanded,
                                <SectionActivities activities={specData.activities}/>
                            )}

                            {specData.files.length > 0 && renderSection(
                                t('specificationViewer.sectionFiles'),
                                filesExpanded,
                                setFilesExpanded,
                                <SpecFiles files={specData.files}/>
                            )}
                            <div ref={bottomSentinelRef}/>
                        </StyledSpecificationViewer>
                    )}

                    {!loading && !error && specData && signatureRequired && (
                        <AcknowledgeBar
                            specificationId={specificationId}
                            reportId={reportId}
                            scrolledToBottom={scrolledToBottom}
                            acknowledgment={acknowledgment}
                            onAcknowledged={setAcknowledgment}
                            returnTo={returnTo}
                        />
                    )}
                </StaticPane>
            </StyledIonContent>
            <LocationModal
                isOpen={showLocationModal}
                onClose={() => setShowLocationModal(false)}
                filterClientId={clientId}
                onSuccess={() => {
                    setShowLocationModal(false);
                    loadData();
                }}
            />
        </IonPage>
    );
};

export default SpecificationViewerPage;
